import Papa from "papaparse";
import { AddressRecord } from "@/types/address.types";
import { validateAddressRecord } from "@/utils/validation.utils";

export class CSVParserService {
  // 定义预期的字段列表
  private expectedFields = ['call', 'address', 'zip', 'note', 'outsea'];

  parseCSV(content: string): AddressRecord[] {
    // 移除 UTF-8 BOM 字符（如果存在）
    const cleanedContent = content.startsWith('﻿') ? content.slice(1) : content;
    
    // 预处理：检查并处理重复表头
    const lines = cleanedContent.split('\n').filter(line => line.trim() !== '');
    if (lines.length < 2 || !lines[0]) {
      return [];
    }

    // 获取表头行并处理重复项
    let headerLine = lines[0].trim();
    const headers = headerLine.split(',').map(h => h.trim());
    
    // 检查重复表头并去重，只保留第一个出现的有效表头
    const uniqueHeaders = new Map<string, boolean>();
    const processedHeaders: string[] = [];
    
    headers.forEach(header => {
      const normalizedHeader = header.toLowerCase();
      if (this.expectedFields.includes(normalizedHeader)) {
        if (!uniqueHeaders.has(normalizedHeader)) {
          uniqueHeaders.set(normalizedHeader, true);
          processedHeaders.push(normalizedHeader);
        }
        // 跳过重复的预期字段
      } else {
        // 保留非预期字段，但不处理
        processedHeaders.push(header);
      }
    });
    
    // 如果有重复表头，重建表头行并使用新内容进行解析
    let contentToParse = cleanedContent;
    if (processedHeaders.length !== headers.length) {
      headerLine = processedHeaders.join(',');
      // 重建完整的 CSV 内容
      contentToParse = [headerLine, ...lines.slice(1)].join('\n');
    }
    
    const result = Papa.parse<AddressRecord>(contentToParse, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header: string) => {
        const trimmed = header.trim().toLowerCase();
        // 只返回预期字段，其他字段返回原始值
        return this.expectedFields.includes(trimmed) ? trimmed : header.trim();
      },
    });

    if (result.errors.length > 0) {
      // 抛出更明确的错误信息
      const errorMessages = result.errors.map(err => 
        `行 ${err.row !== undefined ? err.row + 1 : '未知'}: ${err.message}`
      ).join('; ');
      throw new Error(`CSV 解析失败: ${errorMessages}`);
    }

    // 确保返回的记录只包含预期字段，并且字段名正确
    const normalizedRecords = result.data.map(record => {
      const normalizedRecord: Partial<AddressRecord> = {};
      
      // 只保留预期字段，处理可能的重命名情况
      this.expectedFields.forEach(field => {
        // 检查原始字段和可能的重命名字段（如 field_1）
        let value: string | undefined;
        
        if (record[field as keyof typeof record] !== undefined) {
          value = record[field as keyof typeof record] as string;
        } else {
          // 尝试查找重命名字段（如 field_1）
          const renamedField = `${field}_1`;
          if (record[renamedField as keyof typeof record] !== undefined) {
            value = record[renamedField as keyof typeof record] as string;
          }
        }
        
        if (value !== undefined) {
          normalizedRecord[field as keyof AddressRecord] = value;
        }
      });
      
      return normalizedRecord as AddressRecord;
    }).filter((record) => validateAddressRecord(record));

    // 处理重复呼号，保留最后一个出现的记录
    const uniqueRecordsMap = new Map<string, AddressRecord>();
    normalizedRecords.forEach(record => {
      // 使用呼号作为键，相同呼号的记录会覆盖，保留最后一个
      uniqueRecordsMap.set(record.call, record);
    });

    return Array.from(uniqueRecordsMap.values());
  }

  async parseCSVFile(file: File): Promise<AddressRecord[]> {
    // 先读取文件内容
    const content = await file.text();
    // 调用已有的 parseCSV 方法，该方法已处理 BOM
    return this.parseCSV(content);
  }
}
